import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Avatar,
  Tooltip,
  LinearProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  InputAdornment,
  Switch,
  FormControlLabel,
  Badge,
} from '@mui/material';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as AddressIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Hotel as ReservationIcon,
  Star as VIPIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Message as MessageIcon,
  History as HistoryIcon,
  CardGiftcard as LoyaltyIcon,
  LocalActivity as TagIcon,
  Note as NoteIcon,
  Upload as ImportIcon,
  Download as ExportIcon,
  Merge as MergeIcon,
  PersonAdd as PersonAddIcon,
  Groups as GroupIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`guests-tabpanel-${index}`}
      aria-labelledby={`guests-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const GuestStatusChip = ({ isActive, isBlocked }: { isActive: boolean; isBlocked: boolean }) => {
  if (isBlocked) {
    return <Chip label="Blocked" color="error" size="small" icon={<BlockIcon />} />;
  }
  return <Chip 
    label={isActive ? "Active" : "Inactive"} 
    color={isActive ? "success" : "default"} 
    size="small" 
    icon={isActive ? <ActiveIcon /> : <InactiveIcon />}
  />;
};

const LoyaltyTierChip = ({ tier }: { tier: string }) => {
  const getColor = () => {
    switch (tier) {
      case 'Diamond': return '#b9f2ff';
      case 'Platinum': return '#e5e4e2';
      case 'Gold': return '#ffd700';
      case 'Silver': return '#c0c0c0';
      case 'Bronze': return '#cd7f32';
      default: return '#grey';
    }
  };

  return (
    <Chip 
      label={tier || 'None'} 
      size="small" 
      sx={{ 
        backgroundColor: getColor(),
        color: '#000',
        fontWeight: 'bold'
      }} 
    />
  );
};

export default function GuestsPage() {
  const [currentTab, setCurrentTab] = useState(0);
  const [guests, setGuests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    loyaltyTier: 'all',
    hasReservations: 'all',
    sortBy: 'name',
    tags: []
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalGuests, setTotalGuests] = useState(0);

  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    totalGuests: 0,
    activeGuests: 0,
    vipGuests: 0,
    blockedGuests: 0,
    newThisMonth: 0,
    returningGuests: 0,
    averageStayLength: 0,
    totalRevenue: 0,
    loyaltyMembers: 0
  });

  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA'
    },
    preferences: {
      emailMarketing: true,
      smsNotifications: true,
      language: 'en',
      currency: 'USD'
    },
    tags: [],
    notes: '',
    isVIP: false,
    isBlocked: false,
    blockReason: ''
  });

  useEffect(() => {
    fetchDashboardStats();
    fetchGuests();
  }, [currentTab, filters, page, rowsPerPage]);

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/guests/stats');
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Error fetching guest stats:', error);
    }
  };

  const fetchGuests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        sortBy: filters.sortBy
      });
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.loyaltyTier !== 'all') params.append('loyaltyTier', filters.loyaltyTier);
      if (filters.hasReservations !== 'all') params.append('hasReservations', filters.hasReservations);

      const response = await api.get(`/guests?${params}`);
      setGuests(response.data.guests || []);
      setTotalGuests(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching guests:', error);
      setGuests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleGuestClick = async (guest: any) => {
    try {
      const response = await api.get(`/guests/${guest._id}`);
      setSelectedGuest(response.data);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Error fetching guest details:', error);
    }
  };

  const handleAddGuest = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA'
      },
      preferences: {
        emailMarketing: true,
        smsNotifications: true,
        language: 'en',
        currency: 'USD'
      },
      tags: [],
      notes: '',
      isVIP: false,
      isBlocked: false,
      blockReason: ''
    });
    setIsEditing(false);
    setAddEditOpen(true);
  };

  const handleEditGuest = (guest: any) => {
    setFormData({
      firstName: guest.firstName,
      lastName: guest.lastName,
      email: guest.email,
      phone: guest.phone,
      address: guest.address || {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA'
      },
      preferences: guest.preferences || {
        emailMarketing: true,
        smsNotifications: true,
        language: 'en',
        currency: 'USD'
      },
      tags: guest.tags || [],
      notes: guest.notes || '',
      isVIP: guest.isVIP || false,
      isBlocked: guest.isBlocked || false,
      blockReason: guest.blockReason || ''
    });
    setIsEditing(true);
    setSelectedGuest(guest);
    setAddEditOpen(true);
  };

  const handleSaveGuest = async () => {
    try {
      if (isEditing) {
        await api.put(`/guests/${selectedGuest._id}`, formData);
      } else {
        await api.post('/guests', formData);
      }
      setAddEditOpen(false);
      fetchGuests();
    } catch (error) {
      console.error('Error saving guest:', error);
      alert('Failed to save guest. Please check the form and try again.');
    }
  };

  const handleDeleteGuest = async (guestId: string) => {
    if (window.confirm('Are you sure you want to delete this guest? This action cannot be undone.')) {
      try {
        await api.delete(`/guests/${guestId}`);
        fetchGuests();
      } catch (error) {
        console.error('Error deleting guest:', error);
        alert('Failed to delete guest.');
      }
    }
  };

  const handleBlockToggle = async (guest: any) => {
    const newBlockedStatus = !guest.isBlocked;
    const reason = newBlockedStatus ? 
      prompt('Please provide a reason for blocking this guest:') : 
      null;
    
    if (newBlockedStatus && !reason) return;

    try {
      await api.put(`/guests/${guest._id}/block`, {
        isBlocked: newBlockedStatus,
        blockReason: reason
      });
      fetchGuests();
    } catch (error) {
      console.error('Error updating guest block status:', error);
    }
  };

  const renderDashboard = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <PeopleIcon />
              </Avatar>
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Guests
                </Typography>
                <Typography variant="h5">
                  {dashboardStats.totalGuests}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                <ActiveIcon />
              </Avatar>
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Active Guests
                </Typography>
                <Typography variant="h5">
                  {dashboardStats.activeGuests}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                <VIPIcon />
              </Avatar>
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  VIP Guests
                </Typography>
                <Typography variant="h5">
                  {dashboardStats.vipGuests}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                <LoyaltyIcon />
              </Avatar>
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Loyalty Members
                </Typography>
                <Typography variant="h5">
                  {dashboardStats.loyaltyMembers}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderGuestsTable = () => (
    <Paper>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or phone"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
                <MenuItem value="vip">VIP</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Loyalty Tier</InputLabel>
              <Select
                value={filters.loyaltyTier}
                onChange={(e) => handleFilterChange('loyaltyTier', e.target.value)}
                label="Loyalty Tier"
              >
                <MenuItem value="all">All Tiers</MenuItem>
                <MenuItem value="Diamond">Diamond</MenuItem>
                <MenuItem value="Platinum">Platinum</MenuItem>
                <MenuItem value="Gold">Gold</MenuItem>
                <MenuItem value="Silver">Silver</MenuItem>
                <MenuItem value="Bronze">Bronze</MenuItem>
                <MenuItem value="None">No Membership</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                label="Sort By"
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="created">Date Added</MenuItem>
                <MenuItem value="lastVisit">Last Visit</MenuItem>
                <MenuItem value="totalSpent">Total Spent</MenuItem>
                <MenuItem value="visits">Visit Count</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddGuest}
              >
                Add Guest
              </Button>
              <IconButton onClick={fetchGuests}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Guest</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Loyalty</TableCell>
              <TableCell>Visits</TableCell>
              <TableCell>Total Spent</TableCell>
              <TableCell>Last Visit</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : guests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary">No guests found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              guests.map((guest) => (
                <TableRow 
                  key={guest._id}
                  hover
                  onClick={() => handleGuestClick(guest)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2 }}>
                        {guest.firstName?.[0]}{guest.lastName?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {guest.firstName} {guest.lastName}
                          {guest.isVIP && (
                            <VIPIcon sx={{ ml: 1, fontSize: 16, color: 'warning.main', verticalAlign: 'middle' }} />
                          )}
                        </Typography>
                        {guest.tags?.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {guest.tags.slice(0, 2).map((tag: string) => (
                              <Chip key={tag} label={tag} size="small" variant="outlined" />
                            ))}
                            {guest.tags.length > 2 && (
                              <Chip label={`+${guest.tags.length - 2}`} size="small" variant="outlined" />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{guest.email}</Typography>
                    <Typography variant="caption" color="textSecondary">{guest.phone}</Typography>
                  </TableCell>
                  <TableCell>
                    <GuestStatusChip isActive={guest.isActive} isBlocked={guest.isBlocked} />
                  </TableCell>
                  <TableCell>
                    <LoyaltyTierChip tier={guest.loyaltyTier} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{guest.totalVisits || 0}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      ${guest.totalSpent?.toFixed(2) || '0.00'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {guest.lastVisit ? dayjs(guest.lastVisit).format('MMM D, YYYY') : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditGuest(guest);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={guest.isBlocked ? "Unblock" : "Block"}>
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBlockToggle(guest);
                          }}
                        >
                          <BlockIcon color={guest.isBlocked ? "error" : "inherit"} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalGuests}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom>
              Guest Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage guest profiles, preferences, and history
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={() => alert('Import guests feature coming soon')}
            >
              Import
            </Button>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={() => alert('Export guests feature coming soon')}
            >
              Export
            </Button>
          </Stack>
        </Stack>
      </Box>

      {renderDashboard()}

      <Paper sx={{ mt: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="All Guests" />
          <Tab label="VIP Guests" />
          <Tab label="Loyalty Members" />
          <Tab label="Blocked Guests" />
          <Tab label="Analytics" />
        </Tabs>
      </Paper>

      <TabPanel value={currentTab} index={0}>
        {renderGuestsTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {renderGuestsTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {renderGuestsTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        {renderGuestsTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={4}>
        <Alert severity="info">
          Guest analytics coming soon! Will include visit patterns, spending trends, and segmentation analysis.
        </Alert>
      </TabPanel>

      {/* Guest Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Guest Details - {selectedGuest?.firstName} {selectedGuest?.lastName}
        </DialogTitle>
        <DialogContent>
          {selectedGuest && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><EmailIcon /></ListItemIcon>
                    <ListItemText primary={selectedGuest.email} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><PhoneIcon /></ListItemIcon>
                    <ListItemText primary={selectedGuest.phone} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><AddressIcon /></ListItemIcon>
                    <ListItemText 
                      primary={`${selectedGuest.address?.street || ''}`}
                      secondary={`${selectedGuest.address?.city || ''}, ${selectedGuest.address?.state || ''} ${selectedGuest.address?.zipCode || ''}`}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Guest Statistics</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Total Visits"
                      secondary={selectedGuest.totalVisits || 0}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Total Spent"
                      secondary={`$${selectedGuest.totalSpent?.toFixed(2) || '0.00'}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Member Since"
                      secondary={dayjs(selectedGuest.createdAt).format('MMMM D, YYYY')}
                    />
                  </ListItem>
                </List>
              </Grid>
              {selectedGuest.notes && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Notes</Typography>
                  <Typography variant="body2">{selectedGuest.notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<EditIcon />}
            onClick={() => {
              setDetailsOpen(false);
              handleEditGuest(selectedGuest);
            }}
          >
            Edit Guest
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Guest Dialog */}
      <Dialog 
        open={addEditOpen} 
        onClose={() => setAddEditOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? 'Edit Guest' : 'Add New Guest'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Address</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.address.street}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  address: { ...formData.address, street: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.address.city}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  address: { ...formData.address, city: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={formData.address.state}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  address: { ...formData.address, state: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.address.zipCode}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  address: { ...formData.address, zipCode: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Preferences</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.preferences.emailMarketing}
                    onChange={(e) => setFormData({
                      ...formData,
                      preferences: { ...formData.preferences, emailMarketing: e.target.checked }
                    })}
                  />
                }
                label="Email Marketing"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.preferences.smsNotifications}
                    onChange={(e) => setFormData({
                      ...formData,
                      preferences: { ...formData.preferences, smsNotifications: e.target.checked }
                    })}
                  />
                }
                label="SMS Notifications"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isVIP}
                    onChange={(e) => setFormData({ ...formData, isVIP: e.target.checked })}
                  />
                }
                label="VIP Guest"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddEditOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveGuest}
            disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.phone}
          >
            {isEditing ? 'Save Changes' : 'Add Guest'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}